import { ChangeDetectionStrategy, Component, Input, Signal, computed, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { ExtensionTypes } from 'generated/graphql-types';
import { AdvancedMultiSelectBase } from 'src/app/common-components/multi-select/advanced-multi-select-base.directive';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { extensionOptionActions } from '../state/extension-option.actions';
import { extensionOptionSelectors } from '../state/extension-option.reducer';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-extension-advanced-multi-select',
    templateUrl: './extension-advanced-multi-select.component.html',
    standalone: false
})
export class ExtensionAdvancedMultiSelectComponent extends AdvancedMultiSelectBase<ExtensionOption> {
   @Input() set accountIdFilter(value: number | null) {
      this.updateFilter('AccountId', value !== null ? `${value}` : null);
      this.updateFilter('IsArchived', 'false');
   }

   @Input() set extensionTypesToInclude(extensionTypes: ExtensionTypes[]) {
      if (extensionTypes?.length) {
         const inClause = extensionTypes.map((type) => `'${type}'`).join(', ');
         this.updateFilter('Type', `(${inClause})`, 'IN');
      } else {
         this.updateFilter('Type', null);
      }
   }

   labelSignal: Signal<string> = signal('Extensions');

   itemTrackBy = (extension: ExtensionOption) => extension.extensionId;

   readonly triggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<ExtensionOption>>(() => ({
      displayOptions: {
         label: this.labelSignal(),
         displayType: LcsSelectWithAllDisplayTypes.Chips,
         displayWithFn: (item: ExtensionOption) => `${item.number ?? ''} - ${item.name ?? ''}`,
      },
      group: new LcsAdvancedMultiSelectTriggerFormGroup({
         items: new FormControl(this.itemsFromControl(), { nonNullable: true, validators: Validators.required }),
         all: new FormControl(this.allFromControl(), { nonNullable: true }),
      }),
      syncOn: 'save',
   }));

   constructor() {
      super({
         loadAction: extensionOptionActions.load,
         loadFilters: [],
         searchAction: extensionOptionActions.search,
         searchResetAction: extensionOptionActions.searchReset,
         selectFilteredItems: extensionOptionSelectors.selectFilteredItems,
         selectCallState: extensionOptionSelectors.selectCallState,
      });
   }

   isExtensionSelected(selectedExtensions: ExtensionOption[], extension: ExtensionOption) {
      return selectedExtensions.some((r) => {
         return this.itemTrackBy(r) === this.itemTrackBy(extension);
      });
   }

   private updateFilter(key: string, value: string | null, operator: string = '=') {
      this.options.loadFilters = this.options.loadFilters || [];
      this.options.loadFilters = this.options.loadFilters.filter((filter) => !filter.startsWith(key));
      if (value !== null) {
         this.options.loadFilters.push(`${key} ${operator} ${value}`);
      }
   }
}
